import { SvelteMap, SvelteURL } from 'svelte/reactivity'
import { async_get } from '#lib/utils/fetchUtilsAsync.js'
import type { Notification, NotificationState } from './notifications.types'

const POLL_INTERVAL_MS = 5000
const ENDPOINT = '/api/notifications'

export class Notifications {
	items = $state<Notification[]>([])

	/**
	 * id → optimistic state set by {@link #patchState} before the PATCH resolves.
	 *
	 * The poll loop fires every {@link POLL_INTERVAL_MS}. Without this map, a poll
	 * response that arrives between the optimistic flip (`target.state = next`)
	 * and the PATCH resolution would overwrite the local change with whatever the
	 * server last reported. Both directions matter: a previous fix only preserved
	 * locally-READ items, so toggling READ → NEW lost the optimistic value.
	 * {@link #merge} consults this map and applies the pending state regardless
	 * of direction. Entries are deleted in {@link #patchState} on both success
	 * and rollback so the map never accumulates.
	 */
	readonly #pendingOverrides = new SvelteMap<string, NotificationState>()

	#intervalHandle: number | null = null
	#abortController: AbortController | null = null
	readonly #visibilityHandler = () => {
		if (typeof document === 'undefined') return
		if (document.hidden) this.#pause()
		else this.#resume()
	}

	get hasUnread() {
		return this.items.some((n) => n.state === 'NEW')
	}

	/** Start polling. Idempotent. */
	start() {
		if (typeof document === 'undefined') return
		if (this.#intervalHandle) return
		document.addEventListener('visibilitychange', this.#visibilityHandler)
		if (!document.hidden) this.#resume()
	}

	/** Force an immediate poll (instant reflection of a server-side change). */
	async refresh() {
		await this.#poll()
	}

	/** Stop polling, abort in-flight request, remove listener. */
	stop() {
		if (typeof document !== 'undefined') {
			document.removeEventListener('visibilitychange', this.#visibilityHandler)
		}
		this.#pause()
	}

	/** PATCH state to READ. Optimistic; revert on failure. No-op if already READ. */
	async markRead(id: string) {
		const target = this.items.find((n) => n.id === id)
		if (!target || target.state === 'READ') return
		await this.#patchState(target, 'READ')
	}

	/** Flip state (NEW ↔ READ). Optimistic; revert on failure. */
	async toggleState(id: string) {
		const target = this.items.find((n) => n.id === id)
		if (!target) return
		const next: NotificationState = target.state === 'NEW' ? 'READ' : 'NEW'
		await this.#patchState(target, next)
	}

	async #patchState(target: Notification, next: NotificationState) {
		const previous = target.state
		this.#pendingOverrides.set(target.id, next)
		target.state = next
		try {
			const response = await fetch(`${ENDPOINT}/${target.id}/state`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ state: next })
			})
			if (!response.ok) throw new Error(`PATCH failed: ${response.status}`)
			this.#pendingOverrides.delete(target.id)
		} catch (error) {
			target.state = previous
			this.#pendingOverrides.delete(target.id)
			console.error('notifications PATCH failed', error)
		}
	}

	/** Story/test helper — replace items wholesale, no fetch. */
	replace(seed: Notification[]) {
		this.items = seed.map((n) => ({ ...n }))
	}

	async #poll() {
		this.#abortController?.abort()
		this.#abortController = new AbortController()
		try {
			const url = new SvelteURL(ENDPOINT, window.location.origin)
			const data = await async_get(url, false, { signal: this.#abortController.signal })
			if (Array.isArray(data)) this.#merge(data as Notification[])
		} catch (error) {
			// AbortError: poll superseded by next tick. Silent.
			if ((error as DOMException)?.name === 'AbortError') return
			// Non-JSON response (HTML from auth redirect / dev-server fallback). Silent — next poll retries.
			if (
				error instanceof SyntaxError ||
				(error as Error)?.message?.includes('JSON') ||
				(error as Error)?.message?.includes("Unexpected token '<'")
			) {
				return
			}
			console.error('notifications poll failed', error)
		}
	}

	/** Preserve locally-flipped READ on poll — server may still report NEW until PATCH lands. */
	#merge(incoming: Notification[]) {
		const localById = new SvelteMap(this.items.map((n) => [n.id, n]))
		this.items = incoming.map((n) => {
			const pending = this.#pendingOverrides.get(n.id)
			if (pending) return { ...n, state: pending }
			const local = localById.get(n.id)
			if (local && local.state === 'READ' && n.state === 'NEW') {
				return { ...n, state: 'READ' as const }
			}
			return n
		})
	}

	#resume() {
		if (this.#intervalHandle) return
		void this.#poll()
		this.#intervalHandle = window.setInterval(() => void this.#poll(), POLL_INTERVAL_MS)
	}

	#pause() {
		if (this.#intervalHandle) {
			clearInterval(this.#intervalHandle)
			this.#intervalHandle = null
		}
		this.#abortController?.abort()
		this.#abortController = null
	}
}

export const notifications = new Notifications()
